import React, { useEffect, useRef, useState } from "react";
import ClientService from "../../services/ClientService";
import CurrentClientContext from "../../providers/CurrentClientProvider";
import { Box, Button, Grid, List, ListItem, ListItemText } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import MonthSelector from "../../tailwindComponents/ui/dates/month-selector.component";
import EpochMonthService from "../../services/EpochMonth.service";
import ProgressDashboardBase from "../../components/reports/progressDashboard/ProgressDashboardBase.component";
import OtbRecapBase from "../../components/reports/otbRecap/OtbRecapBase.component";
import AuthProvider from "../../providers/AuthProvider";
import { ClassPerformanceReport } from "../../components/reports/classPerformance/ClassPerformanceReport.component";
import { InventoryActionReport } from "../../components/reports/inventoryAction/InventoryActionTableReport.component";
import { InventoryPerformanceReport } from "../../components/reports/inventoryPerformance/InventoryPerformanceTableReport.component";
import { GlobalAnalyticsReport } from "../../components/reports/globalAnalytics/GlobalAnalyticsReport.component";
import { InventoryVarianceReport } from "../../components/reports/inventoryVariance/InventoryVarianceTableReport.component";
import { BuyerPerformanceReport } from "../../components/reports/buyerPerformance/BuyerPerformanceTableReport.component";
import { InventoryHealthReport } from "../../components/reports/inventoryHealth/InventoryHealthTableReport.component";
import { PlanOTBReport } from "../../components/reports/planOTB/PlanOTBTableReport.component";
import { WeeklyActionReport } from "../../components/reports/weeklyAction/WeeklyActionTableReport.component";
import SaintBernardExport from "../../components/reports/custom/SaintBernardExport.component";
import CustomGoalsReport from "../../components/reports/custom/CustomGoalsReport.component";
import MRFReport from "../../components/reports/custom/MRFReport.component";
import MKECustomReport from "../../components/reports/custom/MKECustomReport.component";
import YoungworldOtbReport from "../../components/reports/youngWorld/YoungworldOtbReport.component";
import YoungworldNextQuarterReport from "../../components/reports/youngWorld/YoungworldNextQuarterReport.component";
import YoungworldWeeklyReport from "../../components/reports/youngWorld/YoungworldWeeklyReport.component";
import WholeEarthCustomReport from "../../components/reports/custom/WholeEarthCustomReport.component";
import TootsiesClassPerformanceReport from "../../components/reports/custom/Tootsies/TootsiesClassPerformanceReport.component";
import TootsiesInventoryPerformanceReport from "../../components/reports/custom/Tootsies/TootsiesInventoryPerformanceReport.component";
import TootsiesGlobalAnalyticsReport from "../../components/reports/custom/Tootsies/TootsiesGlobalAnalyticsReport.component";
import MonthlyExportReport from "../../components/reports/MonthlyExportReport.component";
import { DataValidationReport } from "../../components/reports/DataValidationReport.component";
import { buildV3ClientDashboardReportsPath } from '../../services/ClientDashboardV3';

interface Project {
    id: string;
    name: string;
    workbooks: Workbook[];
}

interface Workbook {
    id: string;
    name: string;
    views: { contentUrl: string }[];
    project: Project;
}

const todayEpochMonth = EpochMonthService.getTodayEpochMonth();

// Reports a share link can open (`?report=`, written by the weekly planning kit's shareUrl). They are the
// dev-tools betas in the list below, so the link only opens them for the same people.
const SHAREABLE_REPORTS = ['inventory-health', 'plan-otb', 'weekly-action'];

function ClientAnalytics(props: { visualVariant?: string }) {
    const isV3 = props?.visualVariant === 'v3';
    const currentClient = CurrentClientContext.useClient() as any;
    const navigate = useNavigate();
    let { clientId } = useParams<{ clientId: string }>();
    const [projects, setProjects] = useState<Project[]>([]);
    const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
    const [lastMonthDataFileFinalized, setLastMonthDataFileFinalized] = useState<boolean>(false);
    const [epochMonth, setEpochMonth] = useState<number>(todayEpochMonth);
    const [selectedV2Report, setSelectedV2Report] = useState<string | null>(null);

    const auth = AuthProvider.useAuth() as any;
    const isAdmin = Boolean(auth?.checkPermission?.('role-create'));
    const isDev = Boolean(auth?.checkPermission?.('dev-tools'));
    const isSaintBernard = currentClient?.client?.Id == 2763;
    const isYoungworld = currentClient?.client?.Id == 3298;
    const isTootsies = currentClient?.client?.Id == 3397;


    useEffect(() => {
        document.title = 'Retail ORBIT: Client Analytics';
    }, []);

    // A share link names its report in `?report=`: open it once, so "Back to Analytics" still returns to the list.
    const linkedReportHandled = useRef(false);
    useEffect(() => {
        if (linkedReportHandled.current || !isDev) return;
        linkedReportHandled.current = true;
        const linked = new URLSearchParams(window.location.search).get('report');
        if (linked && SHAREABLE_REPORTS.includes(linked)) setSelectedV2Report(linked);
    }, [isDev]);

    // SPS-5721 QA fix 3: keep `?report=` in step with the open report, so a reload opens the report on screen (not the one a
    // share link or an earlier visit named). A report that is not shareable, or the list, removes it. A shared view (`v`) belongs
    // to the report that was open, so it goes when that report changes or closes; it is left alone when the first report opens
    // (a share link's own `v`).
    const syncedReport = useRef<string | null>(null);
    useEffect(() => {
        const previous = syncedReport.current;
        if (previous === selectedV2Report) return;
        syncedReport.current = selectedV2Report;
        try {
            const url = new URL(window.location.href);
            if (selectedV2Report && SHAREABLE_REPORTS.includes(selectedV2Report)) url.searchParams.set('report', selectedV2Report);
            else url.searchParams.delete('report');
            if (previous !== null) url.searchParams.delete('v');
            window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
        } catch {
            // No history access (a sandboxed frame): the page works without the address bar following the report.
        }
    }, [selectedV2Report]);

    useEffect(() => {
        async function loadData() {
            if (currentClient.client) {
                await getProjects();

                // get last month data finalized status
                let _lastMonthDataFileFinalized = await ClientService.getLastMonthDataFileFinalized(currentClient.client.Id);

                if (_lastMonthDataFileFinalized === null) {
                    _lastMonthDataFileFinalized = false;
                } else {
                    _lastMonthDataFileFinalized = _lastMonthDataFileFinalized.last_month_data_file_finalized;
                }

                setLastMonthDataFileFinalized(_lastMonthDataFileFinalized);
            }
        }

        loadData();
    }, [currentClient.client]);

    async function getProjects() {
        // Tableau is deprecated - just set empty arrays
        console.log('📊 Tableau deprecated - not loading projects');
        setProjects([]);
        setFilteredProjects([]);
    }


    // Debug logging for render
    console.log('📊 RENDER - projects:', projects.length, 'filteredProjects:', filteredProjects.length);

    const selectedReportLabel = selectedV2Report
        ? REPORT_LABELS[selectedV2Report] || 'Analytics Report'
        : null;

    const lastMonthFinalizedSummary = lastMonthDataFileFinalized
        ? 'Last month data file is finalized for analytics comparisons.'
        : 'Last month data file is not finalized yet; some comparisons may still be in flux.';

    const openReportsLibrary = () => {
        const clientCode = currentClient.client?.ClientCode || currentClient.client?.Code || '';
        if (isV3 && clientCode && clientId) {
            navigate(buildV3ClientDashboardReportsPath(clientCode, clientId));
            return;
        }

        window.location.href = `/clients/${clientCode || clientId}/reports-lib/${clientId}`;
    };

    return <Box sx={{ mt: isV3 ? 0 : 1 }} className={isV3 ? 'v3-analytics-page' : undefined}>
        {isV3 && <Box className="v3-analytics-page__heading">Analytics</Box>}
        {isV3 && <Box className="v3-analytics-page__subtext">Open beta analytics reports, review specialized exports, and jump directly into the active report workspace.</Box>}
        <Grid container sx={{ backgroundColor: isV3 ? 'transparent' : '#fff', minHeight: '300px' }} className={isV3 ? 'v3-data-analytics-grid' : undefined}>
            {!selectedV2Report && (
                <Grid item lg={12} className={isV3 ? 'v3-analytics-page__toolbar' : 'v3-data-card'}>
                    <MonthSelector epochMonth={epochMonth} setEpochMonth={setEpochMonth} minMonth={todayEpochMonth - 48} maxMonth={todayEpochMonth + 1} />
                    {isV3 && (
                        <Box sx={{ px: 2, pb: 2, color: '#667085', fontSize: '0.9rem' }}>
                            {lastMonthFinalizedSummary}
                        </Box>
                    )}
                </Grid>
            )}
            {!selectedV2Report ? <>
                {filteredProjects.length === 0 && projects.length === 0 && (
                    <Grid item lg={12} sx={{ p: 4 }}>
                        <div className={isV3 ? 'v3-analytics-page__callout p-6 text-center' : 'bg-blue-50 border border-blue-200 rounded-lg p-6 text-center'}>
                            <h2 className="text-xl font-semibold text-blue-900 mb-2">
                                Reports Have Moved!
                            </h2>
                            <p className="text-blue-700 mb-4">
                                Analytics reports are now available in the new Reports section.
                            </p>
                            <Button
                                variant="contained"
                                sx={{
                                    backgroundColor: '#422e52',
                                    '&:hover': { backgroundColor: '#5a3d73' }
                                }}
                                onClick={openReportsLibrary}
                            >
                                Go to Reports
                            </Button>
                        </div>
                    </Grid>
                )}
                {filteredProjects.length > 0 && filteredProjects.map(project => {
                    const projectName = project.name === clientId ? "My Reports" : project.name;
                    return (
                        <Grid item sx={jss.box_styles} lg={12 / projects.length} key={project.id} className={isV3 ? 'v3-analytics-page__report-card' : undefined}>
                            <h1 className={isV3 ? 'v3-analytics-page__report-card-header' : 'text-lg uppercase text-center bg-[rgb(145,112,173)] px-5 py-2 text-white'}>
                                {projectName}
                            </h1>
                            <List sx={{ width: '100%' }} className={isV3 ? 'v3-analytics-page__report-list' : undefined}>
                                {project.workbooks.map((value) => (
                                    <ListItem
                                        key={value.id}
                                        disableGutters
                                        sx={jss.list_styles}
                                    >
                                        <ListItemText sx={jss.list_item} primary={value.name} />
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>
                    );
                })}
                {isAdmin &&
                    <Grid item sx={jss.box_styles} lg={12} key={'analytics-reports-v2'} className={isV3 ? 'v3-analytics-page__report-card' : undefined}>
                        <h1 className={isV3 ? 'v3-analytics-page__report-card-header' : 'text-lg uppercase text-center bg-[rgb(145,112,173)] px-5 py-2 text-white'}>
                            Analytics Reports Beta
                        </h1>
                        <List sx={{ width: '100%' }} className={isV3 ? 'v3-analytics-page__report-list' : undefined}>
                            <ListItem
                                key={'progress-dashboard-v2'}
                                disableGutters
                                sx={jss.list_styles}
                                onClick={() => {
                                    setSelectedV2Report('progress-dashboard');
                                }}
                            >
                                <ListItemText sx={jss.list_item} primary={"Progress Report Beta"} />
                            </ListItem>
                            <ListItem
                                key={'otb-recap-v2'}
                                disableGutters
                                sx={jss.list_styles}
                                onClick={() => {
                                    setSelectedV2Report('otb-recap');
                                }}
                            >
                                <ListItemText sx={jss.list_item} primary={"Open To Buy Recap Beta"} />
                            </ListItem>
                            <ListItem
                                key={'class-performance-v2'}
                                disableGutters
                                sx={jss.list_styles}
                                onClick={() => {
                                    setSelectedV2Report('class-performance');
                                }}
                            >
                                <ListItemText sx={jss.list_item} primary={"Class Performance Beta"} />
                            </ListItem>
                            <ListItem
                                key={'inventory-action-v2'}
                                disableGutters
                                sx={jss.list_styles}
                                onClick={() => {
                                    setSelectedV2Report('inventory-action');
                                }}
                            >
                                <ListItemText sx={jss.list_item} primary={"Inventory Action Beta"} />
                            </ListItem>
                            <ListItem
                                key={'inventory-performance-v2'}
                                disableGutters
                                sx={jss.list_styles}
                                onClick={() => {
                                    setSelectedV2Report('inventory-performance');
                                }}
                            >
                                <ListItemText sx={jss.list_item} primary={"Inventory Performance Beta"} />
                            </ListItem>
                            {isDev &&
                                <ListItem
                                    key={'inventory-health-v2'}
                                    disableGutters
                                    sx={jss.list_styles}
                                    onClick={() => {
                                        setSelectedV2Report('inventory-health');
                                    }}
                                >
                                    <ListItemText sx={jss.list_item} primary={"Inventory Health Beta"} />
                                </ListItem>
                            }
                            {isDev &&
                                <ListItem
                                    key={'plan-otb-v2'}
                                    disableGutters
                                    sx={jss.list_styles}
                                    onClick={() => {
                                        setSelectedV2Report('plan-otb');
                                    }}
                                >
                                    <ListItemText sx={jss.list_item} primary={"Plan & OTB Beta"} />
                                </ListItem>
                            }
                            {isDev &&
                                <ListItem
                                    key={'weekly-action-v2'}
                                    disableGutters
                                    sx={jss.list_styles}
                                    onClick={() => {
                                        setSelectedV2Report('weekly-action');
                                    }}
                                >
                                    <ListItemText sx={jss.list_item} primary={"Inventory Action"} />
                                </ListItem>
                            }
                            {isDev &&
                                <ListItem
                                    key={'global-analytics-v2'}
                                    disableGutters
                                    sx={jss.list_styles}
                                    onClick={() => {
                                        setSelectedV2Report('global-analytics');
                                    }}
                                >
                                    <ListItemText sx={jss.list_item} primary={"Global Analytics Beta"} />
                                </ListItem>
                            }
                            {isDev &&
                                <ListItem
                                    key={'inventory-variance-v2'}
                                    disableGutters
                                    sx={jss.list_styles}
                                    onClick={() => {
                                        setSelectedV2Report('inventory-variance');
                                    }}
                                >
                                    <ListItemText sx={jss.list_item} primary={"Inventory Variance Beta"} />
                                </ListItem>
                            }
                            {isDev &&
                                <ListItem
                                    key={'buyer-performance-v2'}
                                    disableGutters
                                    sx={jss.list_styles}
                                    onClick={() => {
                                        setSelectedV2Report('buyer-performance');
                                    }}
                                >
                                    <ListItemText sx={jss.list_item} primary={"Buyer Performance Beta"} />
                                </ListItem>
                            }
                            {isDev &&
                                <ListItem
                                    key={'custom-goals-v2'}
                                    disableGutters
                                    sx={jss.list_styles}
                                    onClick={() => {
                                        setSelectedV2Report('custom-goals');
                                    }}
                                >
                                    <ListItemText sx={jss.list_item} primary={"Custom Goals"} />
                                </ListItem>
                            }
                            {isDev &&
                                <ListItem
                                    key={'mrf-custom-report'}
                                    disableGutters
                                    sx={jss.list_styles}
                                    onClick={() => {
                                        setSelectedV2Report('mrf');
                                    }}
                                >
                                    <ListItemText sx={jss.list_item} primary={"MRF"} />
                                </ListItem>
                            }
                            {isSaintBernard && isDev &&
                                <ListItem
                                    key={'saint-bernard-export'}
                                    disableGutters
                                    sx={jss.list_styles}
                                    onClick={() => {
                                        setSelectedV2Report('saint-bernard-export');
                                    }}
                                >
                                    <ListItemText sx={jss.list_item} primary={"Saint Bernard Export"} />
                                </ListItem>
                            }
                            {isYoungworld && isDev &&
                                <ListItem
                                    key={'youngworld-otb-report'}
                                    disableGutters
                                    sx={jss.list_styles}
                                    onClick={() => {
                                        setSelectedV2Report('youngworld-otb-report');
                                    }}
                                >
                                    <ListItemText sx={jss.list_item} primary={"Youngworld OTB Report"} />
                                </ListItem>
                            }
                            {isYoungworld && isDev &&
                                <ListItem
                                    key={'youngworld-next-quarter-report'}
                                    disableGutters
                                    sx={jss.list_styles}
                                    onClick={() => {
                                        setSelectedV2Report('youngworld-next-quarter-report');
                                    }}
                                >
                                    <ListItemText sx={jss.list_item} primary={"Youngworld Next Quarter Report"} />
                                </ListItem>
                            }
                            {isYoungworld && isDev &&
                                <ListItem
                                    key={'youngworld-weekly-report'}
                                    disableGutters
                                    sx={jss.list_styles}
                                    onClick={() => {
                                        setSelectedV2Report('youngworld-weekly-report');
                                    }}
                                >
                                    <ListItemText sx={jss.list_item} primary={"Youngworld Weekly Report"} />
                                </ListItem>
                            }
                            {isDev &&
                                <ListItem
                                    key={'whole-earth-custom-report'}
                                    disableGutters
                                    sx={jss.list_styles}
                                    onClick={() => {
                                        setSelectedV2Report('whole-earth-custom-report');
                                    }}
                                >
                                    <ListItemText sx={jss.list_item} primary={"Whole Earth Custom Report"} />
                                </ListItem>
                            }
                            {isDev && isTootsies && (
                                <>
                                    <ListItem
                                        key={'tootsies-class-performance-v2'}
                                        disableGutters
                                        sx={jss.list_styles}
                                        onClick={() => {
                                            setSelectedV2Report('tootsies-inventory-performance');
                                        }}
                                    >
                                        <ListItemText sx={jss.list_item} primary={"Tootsies Inventory Performance"} />
                                    </ListItem>
                                    <ListItem
                                        key={'tootsies-global-analytics-v2'}
                                        disableGutters
                                        sx={jss.list_styles}
                                        onClick={() => {
                                            setSelectedV2Report('monthly-export');
                                        }}
                                    >
                                        <ListItemText sx={jss.list_item} primary={"Monthly Export Beta"} />
                                    </ListItem>
                                </>
                            )}
                            {isDev &&
                                <ListItem
                                    key={'data-validation-v2'}
                                    disableGutters
                                    sx={jss.list_styles}
                                    onClick={() => {
                                        setSelectedV2Report('data-validation');
                                    }}
                                >
                                    <ListItemText sx={jss.list_item} primary={"Data Validation Beta"} />
                                </ListItem>
                            }
                        </List>
                    </Grid>
                }
            </> : (
                <Grid item lg={12} className={isV3 ? 'v3-analytics-page__report-stage' : undefined}>
                    {isV3 && (
                        <Box className="v3-analytics-page__report-stage-header">
                            <Box>
                                <Box className="v3-analytics-page__report-stage-title">{selectedReportLabel}</Box>
                                <Box className="v3-analytics-page__report-stage-subtitle">Viewing the selected analytics beta report for the current client and month context.</Box>
                            </Box>
                            <Button variant="outlined" onClick={() => setSelectedV2Report(null)} sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.55)' }}>
                                Back to Analytics
                            </Button>
                        </Box>
                    )}
                    {selectedV2Report === 'progress-dashboard' && (
                        <ProgressDashboardBase clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'otb-recap' && (
                        <OtbRecapBase clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'class-performance' && (
                        <ClassPerformanceReport clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'inventory-action' && (
                        <InventoryActionReport clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'inventory-performance' && (
                        <InventoryPerformanceReport clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'global-analytics' && (
                        <GlobalAnalyticsReport clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'inventory-variance' && (
                        <InventoryVarianceReport clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'buyer-performance' && (
                        <BuyerPerformanceReport clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'inventory-health' && (
                        <InventoryHealthReport clientId={clientId!} epochMonth={epochMonth} setEpochMonth={setEpochMonth} />
                    )}
                    {selectedV2Report === 'plan-otb' && (
                        <PlanOTBReport clientId={clientId!} epochMonth={epochMonth} setEpochMonth={setEpochMonth} />
                    )}
                    {selectedV2Report === 'weekly-action' && (
                        <WeeklyActionReport clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'saint-bernard-export' && isSaintBernard && (
                        <SaintBernardExport clientId={clientId!} />
                    )}
                    {selectedV2Report === 'custom-goals' && (
                        <CustomGoalsReport clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'mrf' && (
                        <MRFReport clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'mke-custom-report' && (
                        <MKECustomReport clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'youngworld-otb-report' && (
                        <YoungworldOtbReport clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'youngworld-next-quarter-report' && (
                        <YoungworldNextQuarterReport clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'youngworld-weekly-report' && (
                        <YoungworldWeeklyReport clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'whole-earth-custom-report' && (
                        <WholeEarthCustomReport clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'tootsies-class-performance' && isDev && isTootsies && (
                        <TootsiesClassPerformanceReport clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'tootsies-inventory-performance' && isDev && isTootsies && (
                        <TootsiesInventoryPerformanceReport clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'tootsies-global-analytics' && isDev && isTootsies && (
                        <TootsiesGlobalAnalyticsReport clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'monthly-export' && isDev && (
                        <MonthlyExportReport clientId={clientId!} epochMonth={epochMonth} />
                    )}
                    {selectedV2Report === 'data-validation' && (
                        <DataValidationReport clientId={clientId!} />
                    )}
                </Grid>
            )}
        </Grid>
    </Box>
}

const jss = {
    head_title: {
        fontSize: 18,
        textTransform: 'uppercase',
        textAlign: 'center',
        backgroundColor: 'rgb(145, 112, 173)',
        padding: '10px 20px',
        color: '#fff'
    },
    box_styles: {
        padding: '0 15px',
        boxSizing: 'border-box'
    },
    list_styles: {
        padding: '5px 15px',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'gainsboro'
        }
    },
    list_item: {},
    date_box: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        marginLeft: '15px',
        marginTop: '15px',
        fontSize: '20px',
        ico: {
            margin: '0 5px 0 15px'
        },
        underscore_item: {
            borderBottom: '1px solid #000',
            cursor: 'pointer',
            fontSize: '14px'
        }
    },
    return_button: {
        textTransform: 'uppercase',
        margin: '5px 0 15px 15px'
    }
};

const REPORT_LABELS: Record<string, string> = {
    'progress-dashboard': 'Progress Report Beta',
    'otb-recap': 'Open To Buy Recap Beta',
    'class-performance': 'Class Performance Beta',
    'inventory-action': 'Inventory Action Beta',
    'inventory-performance': 'Inventory Performance Beta',
    'global-analytics': 'Global Analytics Beta',
    'inventory-variance': 'Inventory Variance Beta',
    'buyer-performance': 'Buyer Performance Beta',
    'custom-goals': 'Custom Goals',
    'mrf': 'MRF',
    'mke-custom-report': 'MKE Custom Report',
    'saint-bernard-export': 'Saint Bernard Export',
    'youngworld-otb-report': 'Youngworld OTB Report',
    'youngworld-next-quarter-report': 'Youngworld Next Quarter Report',
    'youngworld-weekly-report': 'Youngworld Weekly Report',
    'whole-earth-custom-report': 'Whole Earth Custom Report',
    'tootsies-class-performance': 'Tootsies Class Performance',
    'tootsies-inventory-performance': 'Tootsies Inventory Performance',
    'tootsies-global-analytics': 'Tootsies Global Analytics',
    'monthly-export': 'Monthly Export Beta',
    'data-validation': 'Data Validation Beta',
};

export default ClientAnalytics;